// MorrowOS tier gating — the thin, tier-oriented view over the entitlement
// engine. The authority is the backend (`/api/morrow/billing/entitlements`,
// surfaced via useEntitlements); this module just maps the resolved plan tier to
// MorrowOS's ascending product tiers and exposes the `canUseTier` gate + the
// upgrade entry point.
//
// Feature code should prefer `useEntitlements().can(capability)` for specific
// capability checks; `useMorrowPlan()` is for coarse "is this a paid tier?" UI.
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import { useEntitlements } from "./morrowEntitlements";

/** MorrowOS product tiers, ascending in capability. */
export type MorrowTier = "free" | "pay" | "super" | "max";

/** Numeric rank so a higher tier satisfies checks for every lower tier. */
const TIER_RANK: Record<MorrowTier, number> = {
  free: 0,
  pay: 1,
  super: 2,
  max: 3,
};

const asTier = (raw: string): MorrowTier =>
  raw === "pay" || raw === "super" || raw === "max" ? raw : "free";

export interface MorrowPlan {
  /** The user's current (resolved) tier. "free" for guests / while loading. */
  readonly tier: MorrowTier;
  /**
   * Whether the user's current tier is at least `tier`. A "super" user passes
   * `canUseTier("pay")`; everyone passes `canUseTier("free")`.
   */
  readonly canUseTier: (tier: MorrowTier) => boolean;
  /** Convenience flags for the paid tiers. */
  readonly isPay: boolean;
  readonly isSuper: boolean;
  readonly isMax: boolean;
  /** False until the entitlements have loaded; treat gated UI as "free" meanwhile. */
  readonly isLoaded: boolean;
}

/**
 * Resolve the current subject's MorrowOS tier and expose a `canUseTier` gate.
 * Backed by the server-authoritative entitlement engine; collapses to "free"
 * while loading or on error — the correct, least-privileged default.
 */
export function useMorrowPlan(): MorrowPlan {
  const { tier: rawTier, isLoaded } = useEntitlements();

  const tier = useMemo<MorrowTier>(() => asTier(rawTier), [rawTier]);

  const canUseTier = useCallback(
    (required: MorrowTier) => TIER_RANK[tier] >= TIER_RANK[required],
    [tier],
  );

  return {
    tier,
    canUseTier,
    isPay: TIER_RANK[tier] >= TIER_RANK.pay,
    isSuper: TIER_RANK[tier] >= TIER_RANK.super,
    isMax: tier === "max",
    isLoaded,
  };
}

/** Route the pricing / upgrade flow lives at (the in-app Packages page). */
export const PRICING_ROUTE = "/settings/packages" as const;

/**
 * Upgrade entry point. Returns a stable callback that takes the user to the
 * pricing page. Wire this to any "UPGRADE" button:
 *
 *   const upgrade = useUpgrade();
 *   <button onClick={upgrade}>UPGRADE</button>
 */
export function useUpgrade(): () => void {
  const navigate = useNavigate();
  return useCallback(() => {
    void navigate({ to: PRICING_ROUTE });
  }, [navigate]);
}
